export type UnderwaterVisualSettings = {
  toneExposure: number;
  environmentIntensity: number;
  ambientIntensity: number;
  ambientColor: string;
  hemisphereIntensity: number;
  hemisphereSkyColor: string;
  hemisphereGroundColor: string;
  keyIntensity: number;
  keyColor: string;
  fillIntensity: number;
  fillColor: string;
  rimIntensity: number;
  rimColor: string;
  shimmerIntensity: number;
  shimmerColor: string;
  cameraFillIntensity: number;
  cameraFillColor: string;
  causticsOpacity: number;
  causticsScale: number;
  causticsSpeed: number;
  causticsIntensity: number;
  causticsDeepColor: string;
  causticsShallowColor: string;
  causticsHighlightColor: string;
  causticsSurfaceGlowColor: string;
  causticsRadiusScale: number;
  surfaceGlowIntensity: number;
  lightRaysOpacity: number;
  lightRaysColor: string;
  lightRaysWidth: number;
  lightRaysLength: number;
  lightRaysVerticalOffset: number;
  lightRaysSpeed: number;
  lightRaysCount: number;
  bubbleCount: number;
  bubbleSize: number;
  bubbleOpacity: number;
  bubbleSpeed: number;
  bubbleDrift: number;
  bubbleSpread: number;
  particleCount: number;
  particleSize: number;
  particleOpacity: number;
  particleSpeed: number;
  particleDrift: number;
  particleSpread: number;
  depthFogDensity: number;
  depthFogColor: string;
  waterGradientOpacity: number;
  waterGradientTopIntensity: number;
  waterGradientBottomIntensity: number;
  waterGradientHeight: number;
  waterSurfaceColor: string;
  waterSurfaceOpacity: number;
  waterSurfaceHeight: number;
  waterSurfaceWaveStrength: number;
  waterSurfaceWaveScale: number;
  waterSurfaceWaveSpeed: number;
  floorOpacity: number;
  seabedRadius: number;
  seabedDepth: number;
  seabedMurkiness: number;
  seabedTextureScale: number;
  seabedCaustics: number;
  seabedRelief: number;
  seaweedCount: number;
  seaweedArea: number;
  seaweedColor: string;
  seaweedTipColor: string;
  seaweedHeight: number;
  seaweedWidth: number;
  seaweedSway: number;
  seaweedTipLight: number;
  seaweedDepthFade: number;
  pollutionColor: string;
};

export type VisualSettingsSource = {
  get: () => UnderwaterVisualSettings;
};

export type UnderwaterVisualSettingsOverride =
  Partial<UnderwaterVisualSettings>;

export const DEFAULT_UNDERWATER_VISUAL_SETTINGS: UnderwaterVisualSettings = {
  toneExposure: 1.02,
  environmentIntensity: 0.92,
  ambientIntensity: 0.71,
  ambientColor: "#bffaff",
  hemisphereIntensity: 1.29,
  hemisphereSkyColor: "#65e9ff",
  hemisphereGroundColor: "#075c7a",
  keyIntensity: 1.45,
  keyColor: "#ffd8c8",
  fillIntensity: 1.72,
  fillColor: "#20dfff",
  rimIntensity: 10.07,
  rimColor: "#ffdeb6",
  shimmerIntensity: 1.08,
  shimmerColor: "#29eaff",
  cameraFillIntensity: 1.42,
  cameraFillColor: "#c8fbff",
  causticsOpacity: 0.59,
  causticsScale: 0.73,
  causticsSpeed: 0.35,
  causticsIntensity: 0.75,
  causticsDeepColor: "#04506f",
  causticsShallowColor: "#12bad2",
  causticsHighlightColor: "#b8fff0",
  causticsSurfaceGlowColor: "#039fbd",
  causticsRadiusScale: 1.59,
  surfaceGlowIntensity: 0,
  lightRaysOpacity: 0.24,
  lightRaysColor: "#c8ffff",
  lightRaysWidth: 1.35,
  lightRaysLength: 1,
  lightRaysVerticalOffset: 0.5,
  lightRaysSpeed: 1.95,
  lightRaysCount: 30,
  bubbleCount: 250,
  bubbleSize: 1,
  bubbleOpacity: 0.19,
  bubbleSpeed: 0.14,
  bubbleDrift: 0.78,
  bubbleSpread: 0.91,
  particleCount: 1200,
  particleSize: 2.66,
  particleOpacity: 0.86,
  particleSpeed: 0.16,
  particleDrift: 1,
  particleSpread: 1.25,
  depthFogDensity: 0.057,
  waterGradientOpacity: 0.44,
  waterGradientTopIntensity: 1.28,
  waterGradientBottomIntensity: 1,
  waterGradientHeight: -2.3,
  waterSurfaceColor: "#56dfff",
  waterSurfaceOpacity: 0.78,
  waterSurfaceHeight: 7.2,
  waterSurfaceWaveStrength: 0.12,
  waterSurfaceWaveScale: 1.1,
  waterSurfaceWaveSpeed: 0.64,
  floorOpacity: 0.92,
  seabedRadius: 1.71,
  seabedDepth: 0,
  seabedMurkiness: 0.76,
  seabedTextureScale: 1.65,
  seabedCaustics: 0.46,
  seabedRelief: 1.45,
  seaweedCount: 550,
  seaweedArea: 1.93,
  seaweedColor: "#07b687",
  seaweedTipColor: "#28d283",
  seaweedHeight: 1.36,
  seaweedWidth: 1.64,
  seaweedSway: 25.73,
  seaweedTipLight: 1.12,
  seaweedDepthFade: 0.52,
  pollutionColor: "#c4b797",
  depthFogColor: "#6691af",
};

export function createUnderwaterVisualSettings(
  overrides: UnderwaterVisualSettingsOverride = {}
): UnderwaterVisualSettings {
  return {
    ...DEFAULT_UNDERWATER_VISUAL_SETTINGS,
    ...overrides,
  };
}
